import { useCallback } from 'react';

import { faCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/videa-components';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { eventTrackerAtom } from '../atoms/eventTracker';
import type { VoiceNoteTemplateOption } from '../types/api';

interface TemplateListProps {
  templateOptions: VoiceNoteTemplateOption[];
  selectedTemplate: string | undefined;
  className?: string;
  shouldTrackSelection?: boolean;
  onSelectedTemplate: (value: string | undefined) => void;
}

/**
 * Scrollable template list with search at top.
 * Uses Command for search + list with built-in filtering and keyboard support.
 */
export const TemplateList = ({
  templateOptions,
  selectedTemplate,
  className,
  shouldTrackSelection = true,
  onSelectedTemplate,
}: TemplateListProps) => {
  const eventTracker = useAtomValue(eventTrackerAtom);

  const handleSelect = useCallback(
    (value: string) => {
      const id = value || undefined;
      if (id === selectedTemplate) {
        return;
      }
      if (id && shouldTrackSelection) {
        eventTracker.track('template_selected', { template_id: id });
      }
      onSelectedTemplate(id);
    },
    [eventTracker, onSelectedTemplate, selectedTemplate, shouldTrackSelection],
  );

  const filter = useCallback(
    (value: string, search: string) => {
      const template = templateOptions.find((t) => t.id === value);
      if (!template) return 0;
      const query = search.trim().toLowerCase();
      if (!query) return 1;
      return template.name.toLowerCase().includes(query) ? 1 : 0;
    },
    [templateOptions],
  );

  return (
    <div
      className={classNames('vai-flex vai-flex-col vai-min-h-0', className)}
      data-testid="template-list"
    >
      <Command
        className="vai-flex vai-h-full vai-flex-col vai-rounded-md vai-border vai-bg-white"
        filter={filter}
        disablePointerSelection
      >
        <CommandInput placeholder="Search templates" />
        <CommandList className="vai-flex-1 vai-min-h-0 vai-max-h-none vai-overflow-y-auto">
          <CommandEmpty>No templates found.</CommandEmpty>
          {templateOptions.map((template) => {
            const isSelected = selectedTemplate === template.id;
            return (
              <CommandItem
                key={template.id}
                value={template.id}
                onSelect={() => handleSelect(template.id)}
                className={classNames(
                  'vai-flex vai-items-center vai-gap-2 vai-py-2.5 hover:!vai-bg-gray-100',
                  isSelected &&
                    'data-[selected=true]:vai-bg-transparent data-[selected=true]:vai-text-inherit',
                )}
              >
                {isSelected ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="vai-h-4 vai-w-4 vai-flex-shrink-0 vai-text-blue-600"
                  />
                ) : (
                  <span className="vai-w-4 vai-flex-shrink-0" aria-hidden />
                )}
                <span className="vai-truncate">{template.name}</span>
              </CommandItem>
            );
          })}
        </CommandList>
      </Command>
    </div>
  );
};
